import { useState } from "react";
import type { Session } from "../lib/session";
import { useSessionStore } from "../store/sessionStore";
import { useGitHubStore } from "../store/githubStore";
import { createRepo, publishChannel, verifyAccess } from "../lib/github-sync";

interface Props {
  session: Session;
  onClose: () => void;
}

export function SyncModal({ session, onClose }: Props) {
  const { setSyncTarget, replaceChannel } = useSessionStore();
  const { token, setToken } = useGitHubStore();

  const [pat, setPat] = useState(token ?? "");
  const [owner, setOwner] = useState(session.sync_target?.owner ?? "");
  const [repo, setRepo] = useState(session.sync_target?.repo ?? "babyai-playground-channels");
  const [path, setPath] = useState(
    session.sync_target?.path ?? `channels/${session.id}.json`,
  );
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Publishing…");
  const [error, setError] = useState<string | null>(null);
  /** True when verifyAccess returned 404 — the UI flips Publish into
   *  Create & publish to do it in one click. */
  const [repoMissing, setRepoMissing] = useState(false);

  const isPublished = !!session.sync_target;

  function validateInputs(): { pat: string; owner: string; repo: string; path: string } | null {
    setError(null);
    const trimmedPat = pat.trim();
    if (!trimmedPat) {
      setError("A GitHub PAT is required.");
      return null;
    }
    if (!owner.trim() || !repo.trim() || !path.trim()) {
      setError("Owner, repo, and path are all required.");
      return null;
    }
    return {
      pat: trimmedPat,
      owner: owner.trim(),
      repo: repo.trim(),
      path: path.trim(),
    };
  }

  async function publishAt(inputs: { pat: string; owner: string; repo: string; path: string }) {
    setToken(inputs.pat);
    const target = { owner: inputs.owner, repo: inputs.repo, path: inputs.path };
    const { sha } = await publishChannel(target, session, inputs.pat);
    const updated: Session = { ...session, sync_target: { ...target, last_sha: sha } };
    replaceChannel(session.id, updated);
  }

  async function publish() {
    const inputs = validateInputs();
    if (!inputs) return;

    setBusy(true);
    setBusyLabel("Checking access…");
    try {
      const access = await verifyAccess({ owner: inputs.owner, repo: inputs.repo }, inputs.pat);
      if (!access.ok) {
        if (access.kind === "not_found") {
          setRepoMissing(true);
          setError(`${access.reason} — click "Create & publish" to make it.`);
        } else {
          setError(access.reason);
        }
        return;
      }
      setBusyLabel("Publishing…");
      await publishAt(inputs);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function createAndPublish() {
    const inputs = validateInputs();
    if (!inputs) return;

    setBusy(true);
    setBusyLabel("Creating repo…");
    try {
      await createRepo({ owner: inputs.owner, repo: inputs.repo }, inputs.pat);
      setBusyLabel("Publishing…");
      // auto_init makes the repo non-empty so the Contents API doesn't
      // 404 immediately after creation — but propagation can still take
      // a beat. publishChannel handles a 422 the same as 409 via merge
      // retry, so this is robust.
      await publishAt(inputs);
      setRepoMissing(false);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function unpublish() {
    if (!confirm("Stop syncing this channel with GitHub? The remote file is left in place.")) {
      return;
    }
    setSyncTarget(session.id, null);
    onClose();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isPublished ? "Synced" : "Sync with GitHub"}</h2>
        <div className="subtitle">
          GitHub is the shared substrate — every participant with a PAT pulls
          every 10s and pushes their own appends. No server, just a JSON file
          in a repo. Conflicts merge automatically (messages union by id).
        </div>

        <div className="field">
          <label>GitHub PAT (repo scope)</label>
          <input
            type="password"
            value={pat}
            onChange={(e) => setPat(e.target.value)}
            placeholder="ghp_… or github_pat_…"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Owner</label>
          <input
            type="text"
            value={owner}
            onChange={(e) => { setOwner(e.target.value); setRepoMissing(false); }}
            placeholder="your-github-handle"
            disabled={isPublished}
          />
        </div>

        <div className="field">
          <label>Repo</label>
          <input
            type="text"
            value={repo}
            onChange={(e) => { setRepo(e.target.value); setRepoMissing(false); }}
            placeholder="babyai-playground-channels"
            disabled={isPublished}
          />
        </div>

        <div className="field">
          <label>Path in repo</label>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="channels/abc-123.json"
            disabled={isPublished}
          />
        </div>

        {error && <div className="share-warn">{error}</div>}

        {isPublished && (
          <div className="subtitle" style={{ marginTop: 8 }}>
            Synced to{" "}
            <strong>
              {session.sync_target!.owner}/{session.sync_target!.repo}
            </strong>{" "}
            at <strong>{session.sync_target!.path}</strong>.
          </div>
        )}

        <div className="actions">
          {isPublished && (
            <button
              className="btn"
              style={{ marginRight: "auto", color: "var(--danger)" }}
              onClick={unpublish}
            >
              Unpublish
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {!isPublished && !repoMissing && (
            <button className="btn btn-primary" onClick={publish} disabled={busy}>
              {busy ? busyLabel : "Publish"}
            </button>
          )}
          {!isPublished && repoMissing && (
            <button className="btn btn-primary" onClick={createAndPublish} disabled={busy}>
              {busy ? busyLabel : "Create & publish"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
